import React from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { useInitiateRegistration, useUpdateParticipation } from "../dataloader";
import { SchemaField } from "../../../../../shared/form/renderer/types/SchemaField";
import { useRegDetail } from "../../../../../shared/context/regDetail";
import { RegFormContextType } from "../type";
import { RegistrationRoute } from "../../../routes/route";
import { Base64Util } from "../../../../../shared/utils/base64";
import { RegistrationStep } from "shared/utils/enums";
import { errorToast } from "../../../../../shared/utils/toastNotification";
import { useCopies } from "shared/hooks/useCopies";
import { getBulkTicketQuantitySchema } from "apps/registration/components/bulkTicketQuantity/schema";
import { getAllUtmSearchParams, isBulkTicketAdmin, isRegPaymentPending } from "apps/registration/utils";
import { ApiRegistrationParticipantForParticipantResponse } from "../../../../../shared/types/ApiRegistrationParticipantForParticipantResponse";
import { getRegistrationKey } from "shared/utils/config";
import QueryParamKey from "../../../../../shared/enums/QueryParamKey";
import sortBy from "lodash/sortBy";
import { ApiPayForPurchaseOrderResponse } from "../../../../../shared/types/ApiPayForPurchaseOrderResponse";
import InventoryType from "../../../../../shared/enums/InventoryType";
import { InitiateRegService } from "./types";
import { ApiInvoicePaymentRequest } from "shared/types/ApiInvoicePaymentRequest";

/**
 * @name useInitiateRegService
 * @description this includes logic related to Registration Form
 */
export default function useInitiateRegService(): InitiateRegService {
  const copies = useCopies();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const utmSearchParams = React.useMemo(() => getAllUtmSearchParams(searchParams), [searchParams]);

  const { participantObj, setParticipantData, regFormDetailObj, updatePaymentDetails, refetchParticipantDetails } =
    useOutletContext<RegFormContextType>();
  const formRef = React.useRef<any>();
  const { id: registrationId, eventSeriesId, invoicePaymentEnabled } = useRegDetail();
  const { mutateAsync: initiateRegistration } = useInitiateRegistration();
  const { mutateAsync: updateParticipantDetails } = useUpdateParticipation();
  const [currentStep, setCurrentStep] = React.useState<RegistrationStep>(() => {
    if (!participantObj?.id && regFormDetailObj?.validateEmailDomain) {
      return RegistrationStep.EMAIL_VALIDATION;
    }
    return RegistrationStep.DETAILS;
  });

  const isFormDisabled = React.useMemo(
    () => participantObj?.regFormDisabledForParticipant,
    [participantObj?.regFormDisabledForParticipant]
  );

  const bulkTicketFieldConfig = React.useMemo(() => {
    const isBulkTicketAdminForm = isBulkTicketAdmin(
      participantObj ? participantObj?.registrationForm?.subRole : regFormDetailObj?.subRole
    );
    if (isBulkTicketAdminForm) {
      const ticketPurchaseCount = participantObj?.nonAttendeeApplication?.ticketPurchaseCount;
      const unitPriceAmount = participantObj?.regPurchaseOrder?.lines.find(
        line => line.lineType === InventoryType.BULK_TICKET
      )?.unitPriceAmount;
      const applicableRates = unitPriceAmount
        ? []
        : sortBy(
            regFormDetailObj
              ? regFormDetailObj?.rateCard?.applicableRates
              : participantObj?.registrationForm?.rateCard?.applicableRates,
            ["lowerLimit"]
          );
      const schema = getBulkTicketQuantitySchema(copies, applicableRates, ticketPurchaseCount, unitPriceAmount);
      return {
        applicableRates,
        schema
      };
    }
  }, [copies, participantObj, regFormDetailObj]);

  const navigateToConfirmation = React.useCallback(
    regParticipationId => {
      const encodedParticipationId = Base64Util.encode(regParticipationId);
      searchParams.set(QueryParamKey.REG_PARTICIPATION_ID, encodedParticipationId as string);
      navigate(
        {
          pathname: RegistrationRoute.Confirmation,
          search: searchParams.toString()
        },
        { replace: true }
      );
    },
    [navigate, searchParams]
  );

  const paymentSuccessHandler = React.useCallback(
    (data: ApiPayForPurchaseOrderResponse) => {
      updatePaymentDetails(data);
      navigateToConfirmation(participantObj.id);
    },
    [updatePaymentDetails, navigateToConfirmation, participantObj]
  );

  const paymentErrorHandler = React.useCallback(() => {
    refetchParticipantDetails();
  }, [refetchParticipantDetails]);

  const invoicePayerPaymentRequestSuccessHandler = React.useCallback(
    (data: ApiInvoicePaymentRequest) => {
      setParticipantData({
        ...participantObj,
        invoicePayerPaymentRequested: true,
        regPurchaseOrder: data.purchaseOrder
      });
      navigateToConfirmation(participantObj.id);
    },
    [navigateToConfirmation, participantObj, setParticipantData]
  );

  const resendInvoicePaymentReminderSuccessHandler = React.useCallback(() => {
    navigateToConfirmation(participantObj.id);
  }, [navigateToConfirmation, participantObj]);

  const formSchemaList: Array<SchemaField> = React.useMemo(() => {
    const schema = participantObj?.profile || regFormDetailObj?.profile || [];
    if (bulkTicketFieldConfig?.schema) {
      return [...schema, bulkTicketFieldConfig.schema];
    }
    return schema;
  }, [bulkTicketFieldConfig?.schema, participantObj?.profile, regFormDetailObj?.profile]);

  const couponCode = searchParams.get(QueryParamKey.COUPON_CODE) || null;
  const sponsorOrgId = searchParams.get(QueryParamKey.SPONSOR_ORG_ID) || null;

  const initiateReg = React.useCallback(
    async formValues => {
      const apiReqObj = {
        ...formValues,
        registrationId: registrationId,
        registrationFormId: regFormDetailObj?.id,
        couponCode: couponCode ? Base64Util.decode(couponCode) : couponCode,
        sponsorOrganizationId: sponsorOrgId ? Base64Util.decode(sponsorOrgId) : null,
        registrationKey: getRegistrationKey(),
        regRequestParams: utmSearchParams,
        originatingFormId: regFormDetailObj?.originatingFormId
      };
      return initiateRegistration(apiReqObj);
    },
    [
      registrationId,
      regFormDetailObj?.id,
      couponCode,
      sponsorOrgId,
      initiateRegistration,
      utmSearchParams,
      regFormDetailObj?.originatingFormId
    ]
  );

  const updateParticipationDetails = React.useCallback(
    async formValues => {
      const purchaseLineObj = participantObj.regPurchaseOrder?.lines.find(i => i.lineType === InventoryType.TICKET);
      const apiReqObj = {
        ...formValues,
        couponCode: purchaseLineObj?.couponCode,
        sponsorOrganizationId: participantObj.sponsoringRegistrationOrganizations?.[0],
        registrationParticipantId: participantObj.id,
        uiVersion: participantObj.uiVersion,
        registrationId: participantObj.registrationId,
        registrationKey: getRegistrationKey()
      };
      return updateParticipantDetails(apiReqObj);
    },
    [participantObj, updateParticipantDetails]
  );

  const navigateToApplicableView = React.useCallback(
    (regParticipationObj: ApiRegistrationParticipantForParticipantResponse) => {
      if (isRegPaymentPending(regParticipationObj)) {
        setCurrentStep(RegistrationStep.PAYMENT);
      } else {
        navigateToConfirmation(regParticipationObj.id);
      }
    },
    [navigateToConfirmation]
  );

  const submitHandler = React.useCallback(
    async formValues => {
      if (searchParams.get(QueryParamKey.PREVIEW_MODE)) {
        errorToast(copies.get("cannot.create.registration.in.preview.mode"));
        return;
      }
      let data: ApiRegistrationParticipantForParticipantResponse;
      if (participantObj) {
        data = await updateParticipationDetails(formValues);
      } else {
        data = await initiateReg(formValues);
      }
      formRef.current?.resetForm();
      await setParticipantData(data);
      navigateToApplicableView(data);
    },
    [
      searchParams,
      participantObj,
      setParticipantData,
      navigateToApplicableView,
      copies,
      updateParticipationDetails,
      initiateReg
    ]
  );

  const submitButtonText = React.useMemo(() => {
    if (participantObj) {
      return isFormDisabled ? copies.get("continue") : copies.get("update");
    } else if (regFormDetailObj?.applicationFlow) {
      return copies.get("apply");
    } else {
      return copies.get("save");
    }
  }, [copies, regFormDetailObj?.applicationFlow, participantObj, isFormDisabled]);

  const isBTA = React.useMemo(() => isBulkTicketAdmin(participantObj?.subRole), [participantObj?.subRole]);

  return {
    formSchemaList,
    submitHandler,
    formRef,
    participantObj,
    currentStep,
    navigateToApplicableView,
    paymentSuccessHandler,
    paymentErrorHandler,
    registrationId,
    eventSeriesId,
    navigateToConfirmation,
    setCurrentStep,
    formId: regFormDetailObj?.id || participantObj?.registrationForm?.id,
    pageTitle: regFormDetailObj?.title || participantObj?.registrationForm?.title,
    displayNote: regFormDetailObj?.displayNote,
    applicableRates: bulkTicketFieldConfig?.applicableRates,
    submitButtonText,
    isFormDisabled,
    isBTA,
    invoicePaymentEnabled,
    invoicePayerPaymentRequestSuccessHandler,
    resendInvoicePaymentReminderSuccessHandler,
    regFormDetailObj
  };
}
